import React, { useState, useEffect, useRef } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './VoiceAssistant.css';

const VoiceAssistant = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef(null);

  // Need to handle missing context if outside Router, but generally fine inside App
  const navigate = useNavigate();

  useEffect(() => {
    // Load voices early
    if ('speechSynthesis' in window) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onstart = () => {
        setIsListening(true);
      };

      recognitionRef.current.onresult = (event) => {
        const currentTranscript = event.results[0][0].transcript.toLowerCase();
        setTranscript(currentTranscript);
        processCommand(currentTranscript);
      };

      recognitionRef.current.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    } else {
      console.warn("Speech Recognition API not supported in this browser.");
    }
  }, []);

  const speak = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel(); // Cancel any ongoing speech
      const utterance = new SpeechSynthesisUtterance(text);

      const voices = window.speechSynthesis.getVoices();
      // Try to find a female voice
      const femaleVoice = voices.find(voice =>
        voice.name.includes('Female') ||
        voice.name.includes('woman') ||
        voice.name.includes('Zira') ||
        voice.name.includes('Samantha') ||
        voice.name.includes('Victoria') ||
        voice.name.includes('Rishi') // Sometimes male, but typically we want standard Female ones
      );

      if (femaleVoice) {
        utterance.voice = femaleVoice;
      }

      utterance.pitch = 1.1; // Slightly higher pitch often sounds more feminine
      utterance.rate = 1;
      window.speechSynthesis.speak(utterance);
    }
  };

  const routeMap = {
    // Dashboards
    "overall dashboard": "/",
    "home": "/",
    "home page": "/",
    "start page": "/",
    "main dashboard": "/",
    "main page": "/",

    "dashboard": "/Dashboard",
    "grey dashboard": "/Dashboard",

    // FAS / Finance
    "ledger": "/Ledger",
    "account ledger": "/Ledger",
    "laser": "/Ledger", // common mispronunciation

    "trial balance": "/TrialBalance",
    "trial": "/TrialBalance",

    "profit and loss": "/ProfitAndLoss",
    "p and l": "/ProfitAndLoss",
    "profit loss": "/ProfitAndLoss",
    "p n l": "/ProfitAndLoss",

    "balance sheet": "/BalanceSheet",
    "balance seet": "/BalanceSheet",

    "fund and flow": "/FundAndFlow",
    "fund flow": "/FundAndFlow",
    "cash flow": "/FundAndFlow",

    "receivable": "/Receivable",
    "receivables": "/Receivable",
    "outstanding receivable": "/Receivable",

    "payable": "/Payable",
    "payables": "/Payable",
    "outstanding payable": "/Payable",

    "customer": "/Customer",
    "customer profile": "/Customer",
    "customers": "/Customer",

    "supplier": "/Supplier",
    "supplier profile": "/Supplier",
    "suppliers": "/Supplier",

    "bank": "/Bank",
    "bank statement": "/Bank",
    "bank details": "/Bank",

    "sale dashboard": "/SaleDashboard",
    "sales dashboard": "/SaleDashboard",
    "sales page": "/SaleDashboard",
    "sales": "/SaleDashboard",

    "purchase dashboard": "/PurchaseDashboard",
    "purchases dashboard": "/PurchaseDashboard",
    "purchase page": "/PurchaseDashboard",
    "purchase": "/PurchaseDashboard",

    // Statutory
    "g s t r": "/Gstr",
    "gstr": "/Gstr",
    "gst": "/Gstr",

    "t d s": "/Tds",
    "tds": "/Tds",
    "tax deducted": "/Tds",

    // Grey
    "opening": "/Opening",
    "opening balance": "/Opening",

    "balance": "/Balance",
    "grey balance": "/Balance",

    "delivery": "/Delivery",
    "grey delivery": "/Delivery",

    "inward": "/Inward",
    "in word": "/Inward", // pronunciation fix

    "grey inward report": "/GreyInwardReport",
    "grey inward": "/GreyInwardReport",

    "party profile": "/PartyProfile",

    "total balance meter": "/TotalBalanceMeter",
    "balance meter": "/TotalBalanceMeter",

    "day wise": "/Daywise",
    "day wise report": "/Daywise",

    // Analysis & Costing (Colour / Maintenance)
    "abc": "/Abc",
    "a b c": "/Abc",
    "abc analysis": "/AbcAnalysis",
    "a b c analysis": "/AbcAnalysis",

    "fsn": "/Fsn",
    "f s n": "/Fsn",
    "fsn analysis": "/FsnAnalysis",
    "f s n analysis": "/FsnAnalysis",

    "stock": "/Stock",
    "stock analysis": "/StockAnalysis",
    "monthly stock": "/MonthlyStock",

    "machine group wise costing": "/MachineGroupWiseCosting",
    "machine costing": "/MachineGroupWiseCosting",

    // Payroll
    "salary analysis dashboard": "/SalaryAnalysisDashboard",
    "salary dashboard": "/SalaryAnalysisDashboard",

    "salary statement": "/SalaryStatement",

    "department wise salary summary": "/DepWiseSalarySummary",
    "department salary": "/DepWiseSalarySummary",

    "employee profile": "/EmployeeProfile",
    "employee": "/EmployeeProfile",
    "staff profile": "/EmployeeProfile",

    "salary summary": "/SalarySummary",

    "pt statement": "/PTStatement",
    "p t statement": "/PTStatement",
    "professional tax": "/PTStatement",

    "esi statement": "/ESIStatement",
    "e s i statement": "/ESIStatement",

    "pf statement": "/PFStatement",
    "p f statement": "/PFStatement",
    "provident fund": "/PFStatement",

    "esi pf pt summary": "/ESI_PF_PT_Summary",
    "e s i p f p t summary": "/ESI_PF_PT_Summary",

    "department wise present": "/DepartmentWisePresent",
    "department presence": "/DepartmentWisePresent",
    "attendance": "/DepartmentWisePresent",

    // Millstage
    "grey stage balance": "/GreyStageBalance",
    "stage wise in out balance": "/StageWiseInOutBalance",
    "loot wise report": "/LootWiseReport",
    "group wise stage wise balance": "/GroupWiseStageWiseBalance",
    "group wise stage detail report": "/GroupWiseStageDetailReport",
    "group wise party wise detail report": "/GroupWisePartyWiseDetailReport",
    "machine wise batch detail": "/MachineWiseBatchDetail",

    // Other Pages
    "order approval": "/OrderApproval",
    "approve order": "/OrderApproval",
    "orders": "/OrderApproval",

    "collection man": "/CollectionMan",
    "collection": "/CollectionMan",
    "collections": "/CollectionMan",

    "user right": "/UserRight",
    "user rights": "/UserRight",
    "permissions": "/UserRight",
    "user permissions": "/UserRight",

    // Remaining FAS Sales & Purchases
    "month wise party wise sale": "/MonthWisePartyWiseSale",
    "party wise item wise sale": "/PartyWiseItemWiseSale",
    "production group wise": "/ProductionGroupWise",
    "item wise month wise": "/ItemWiseMonthWise",
    "item wise summary": "/ItemWiseSummary",
    "party item wise summary": "/PartyItemWiseSummary",
    "month item party": "/MonthItemParty",
    "party wise quarter": "/PartyWiseQuarter",
    "month wise party wise purchase": "/MonthWisePartyWisePurchase",
    "party wise item wise purchase": "/PartyWiseItemWisePurchase",
    "month wise book wise": "/MonthWiseBookWise",
    "book wise sub book wise": "/BookWiseSubBookWise",
    "book wise monthly analysis": "/BookWiseMonthlyAnylysis",
    "book wise": "/BookWise",

    // Remaining Colour / Maintenance Inward & Consumption
    "inward analysis": "/InwardAnalysis",
    "master wise machine group costing": "/MasterWiseMachineGroupCosting",
    "machine group with machine costing": "/MachineGroupWithMachineCosting",
    "machine group costing summary": "/MachineGroupCostingSummary",
    "master wise monthly consumption": "/MasterWiseMonthlyConsumption",
    "machine group cost analysis": "/MachineGroupCostAnlysis",
    "machine group batch costing": "/MachineGroupBatchCosting",
    "master wise consumption": "/MasterWiseConsumption",
    "master wise costing": "/MasterWiseCosting",
    "item supplier analysis": "/ItemSupplierAnaylysisMain",
    "party item analysis": "/PartyItemAnalysisMain",
    "inward analysis m": "/InwardAnalysism",
    "inward item wise": "/InwardItemWise",
    "issue item wise": "/IssueItemWise",
    "item wise machine wise consumption": "/ItemWiseMachineWiseConsumption",
    "machine machine group wise consumption": "/MachineMachineGroupWiseConsumtion",
    "machine wise consumption": "/MachineWiseConsumption",
    "machine group wise consumption": "/MachineGroupWiseConsumtion",
    "machine group month wise consumption": "/MachineGroupMonthWiseConsumption",
    "item wise daily and monthly": "/itemWiseDailyAndMonthly"
  };

  const processCommand = (command) => {
    if (command.includes('hello') || command.includes('hi')) {
      speak("Hello! How can I help you today?");
      return;
    }

    if (command.includes('time')) {
      const timeString = new Date().toLocaleTimeString();
      speak(`The current time is ${timeString}`);
      return;
    }

    if (command.includes('your name')) {
      speak("I am your personal voice assistant.");
      return;
    }

    if (command.includes('stop') || command.includes('shut up')) {
      speak("Okay, stopping.");
      return;
    }

    if (command.includes('dark mode') || command.includes('light mode') || command.includes('theme') || command.includes('dark') || command.includes('light')) {
      const isDarkMode = command.includes('dark');
      const newTheme = isDarkMode ? 'dark' : 'light';

      window.dispatchEvent(new CustomEvent('themeChange', { detail: newTheme }));

      speak(`Switched to ${newTheme} mode.`);
      return;
    }

    // Special case for opening a specific party's ledger, party profile, or inward, delivery, balance, opening
    const partyPagesRegex = /(?:ledger|lazer|laser|party profile|grey profile|gray profile|inward|in word|delivery|balance|opening|customer|supplier)/;
    const exactMatches = [
      'ledger', 'open ledger', 'show ledger', 'lazer', 'open lazer', 'show lazer', 'laser', 'open laser', 'show laser',
      'party profile', 'open party profile', 'show party profile', 'grey profile', 'open grey profile', 'gray profile',
      'inward', 'open inward', 'show inward', 'in word', 'open in word',
      'delivery', 'open delivery', 'show delivery',
      'balance', 'open balance', 'show balance',
      'opening', 'open opening', 'show opening',
      'customer', 'open customer', 'show customer',
      'supplier', 'open supplier', 'show supplier'
    ];

    if (partyPagesRegex.test(command) && !exactMatches.includes(command)) {
      let partyName = null;
      let targetRoute = '/Ledger'; // default
      let targetName = 'ledger';

      if (command.includes('party profile') || command.includes('grey profile') || command.includes('gray profile')) {
        targetRoute = '/PartyProfile';
        targetName = 'party profile';
      } else if (command.includes('customer')) {
        targetRoute = '/Customer';
        targetName = 'customer';
      } else if (command.includes('supplier')) {
        targetRoute = '/Supplier';
        targetName = 'supplier';
      } else if (command.includes('inward') || command.includes('in word')) {
        targetRoute = '/Inward';
        targetName = 'inward';
      } else if (command.includes('delivery')) {
        targetRoute = '/Delivery';
        targetName = 'delivery';
      } else if (command.includes('balance')) {
        targetRoute = '/Balance';
        targetName = 'balance';
      } else if (command.includes('opening') && !command.startsWith('opening ')) {
        // Prevent matching just the verb "opening" at the start unless it's strictly "opening of XYZ"
        // But since we are here, we know it passed the regex.
        targetRoute = '/Opening';
        targetName = 'opening';
      }

      const matchOf = command.match(new RegExp(`(?:${targetName}|ledger|lazer|laser|party profile|grey profile|inward|in word|delivery|balance|opening|customer|supplier) (?:of|for) (.*)`));
      const matchKa = command.match(new RegExp(`(.*) ka (?:${targetName}|ledger|lazer|laser|party profile|grey profile|inward|in word|delivery|balance|opening|customer|supplier)`));

      if (matchOf && matchOf[1]) {
        partyName = matchOf[1].trim();
      } else if (matchKa && matchKa[1]) {
        partyName = matchKa[1].replace(/^(open|show|opening)\s+/i, '').trim();
      } else {
        partyName = command.replace(/^(open|show|opening)\s+/i, '')
          .replace(/ledger/g, '').replace(/lazer/g, '').replace(/laser/g, '')
          .replace(/party profile/g, '').replace(/grey profile/g, '').replace(/gray profile/g, '')
          .replace(/inward/g, '').replace(/in word/g, '')
          .replace(/delivery/g, '').replace(/balance/g, '')
          .replace(/customer/g, '').replace(/supplier/g, '')
          // We don't blindly replace 'opening' here because it might strip the verb 'opening' and the page name 'opening'. 
          // But since it's at the end of the replace chain, it's safer. Let's just strip 'opening' from the end.
          .replace(/\s+opening$/g, '')
          .trim();
      }

      if (partyName && partyName !== 'account' && partyName !== 'general' && partyName.length > 0) {
        speak(`Opening ${targetName} for ${partyName}`);
        navigate(targetRoute, { state: { searchParty: partyName } });
        return;
      }
    }

    // Check for open commands
    let foundRoute = false;
    if (command.includes('open') || command.includes('go to') || command.includes('show')) {
      for (const [pageName, route] of Object.entries(routeMap)) {
        if (command.includes(pageName)) {
          speak(`Opening ${pageName}`);
          navigate(route);
          foundRoute = true;
          break; // Stop after first match
        }
      }
    } else {
      // Fallback: check if they just said the page name directly
      for (const [pageName, route] of Object.entries(routeMap)) {
        if (command.includes(pageName)) {
          speak(`Navigating to ${pageName}`);
          navigate(route);
          foundRoute = true;
          break;
        }
      }
    }

    if (!foundRoute) {
      speak(`I heard: ${command}, but I don't know how to do that yet.`);
    }
  };

  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      if (recognitionRef.current) {
        setTranscript('');
        try {
          recognitionRef.current.start();
        } catch (e) {
          console.error(e);
        }
      } else {
        alert("Your browser does not support Speech Recognition.");
      }
    }
  };

  if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
    return null; // Don't render anything if not supported
  }

  return (
    <div className="voice-assistant-container">
      <button
        className={`voice-assistant-btn ${isListening ? 'listening' : ''}`}
        onClick={toggleListening}
        title={isListening ? "Listening..." : "Click to speak"}
      >
        {isListening ? (
          <>
            <div className="wave-ripple ripple-1"></div>
            <div className="wave-ripple ripple-2"></div>
            <div className="wave-ripple ripple-3"></div>
            <div className="btn-icon-wrapper active">
              <Mic size={22} color="white" className="mic-icon animate-pulse" />
            </div>
          </>
        ) : (
          <div className="btn-icon-wrapper">
            <MicOff size={22} color="white" className="mic-icon" />
          </div>
        )}
      </button>
    </div>
  );
};

export default VoiceAssistant;
